import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Buildings, MapPin, Trash, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Goods Receipts list — history of every vendor invoice received against
 * warehouse and/or site. Each row summarises destination split and total.
 */
export default function GoodsReceiptsList() {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(null); // { id, receipt_number } while confirming delete
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/goods-receipts");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/goods-receipts/${pending.id}`);
      const msg =
        `Deleted ${data.receipt_number} — reversed ${data.reversed_warehouse_lines} warehouse line(s) ` +
        `and ${data.reversed_purchases} site purchase(s).`;
      if (data.warnings?.length) {
        toast.warning(`${msg} Warning: ${data.warnings.join("; ")}`);
      } else {
        toast.success(msg);
      }
      setPending(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="goods-receipts-page">
      <PageHeader
        title="Goods Receipts"
        subtitle="Every vendor invoice you've received — with per-line destination trail."
        module="Warehouse · Inbound"
        ribbon={
          <>
            <BcRibbonGroup label="New">
              <Link to="/goods-receipts/new">
                <BcRibbonBtn icon={Plus} label="New Receipt" variant="primary" testid="ribbon-new-goods-receipt" />
              </Link>
            </BcRibbonGroup>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
            </BcRibbonGroup>
          </>
        }
      />
      <div className="p-6">
        {rows.length === 0 ? (
          <EmptyState
            title="No goods receipts yet"
            description="Click New Receipt to upload a vendor invoice — AI will extract every line and let you route each to warehouse or site."
          />
        ) : (
          <div className="bc-card overflow-hidden" data-testid="goods-receipts-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>GRN #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead>Lines</TableHead>
                  <TableHead>Destinations</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`grn-row-${r.id}`}>
                    <TableCell className="font-mono-num font-medium">{r.receipt_number}</TableCell>
                    <TableCell>{r.vendor}</TableCell>
                    <TableCell className="font-mono-num text-[#605E5C]">{r.invoice_number || "—"}</TableCell>
                    <TableCell className="text-[#605E5C]">{fmtDate(r.invoice_date) || "—"}</TableCell>
                    <TableCell><span className="font-mono-num">{r.line_count}</span></TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {r.warehouse_line_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-sky-50 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded-sm">
                            <Buildings size={10} /> {r.warehouse_line_count} whs
                          </span>
                        )}
                        {r.site_line_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-sm">
                            <MapPin size={10} /> {r.site_line_count} site
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(r.total)}</TableCell>
                    <TableCell className="text-[12px] text-[#605E5C]">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => setPending({ id: r.id, receipt_number: r.receipt_number, vendor: r.vendor, total: r.total })}
                        data-testid={`delete-grn-${r.id}`}
                        title="Delete receipt & reverse stock/purchases"
                        className="text-slate-400 hover:text-red-600 p-1"
                      >
                        <Trash size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => !o && !busy && setPending(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-[#A4262C]">
              <Warning size={20} weight="fill" /> Delete {pending?.receipt_number}?
            </DialogTitle>
          </DialogHeader>
          <div className="text-[13px] text-[#323130] space-y-2">
            <p>
              This will <b>reverse every stock addition</b> from this receipt and delete any linked site purchases.
              If the material has already been issued to a job, stock levels may go negative.
            </p>
            <ul className="text-[12px] text-[#605E5C] list-disc pl-5 space-y-0.5">
              <li>Vendor: <b>{pending?.vendor}</b></li>
              <li>Total: <b>{fmtMoney(pending?.total || 0)}</b></li>
            </ul>
            <p className="text-[12px] text-[#8A6E00] bg-[#FFF4CE] border border-[#F7C948] px-3 py-2 rounded-sm">
              This cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={busy} data-testid="cancel-delete-grn">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={busy}
              data-testid="confirm-delete-grn"
              className="bg-[#A4262C] hover:bg-[#8B2027] text-white rounded-sm"
            >
              {busy ? "Deleting…" : "Delete & Reverse"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
